import { Injectable } from '@angular/core';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { IUser } from '@shared/interfaces/user.interface';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { UsersService } from '@shared/services/users.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  constructor(
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _users: UsersService
  ) {}

  /**
   * Persists the user's preferred language. When the chosen language matches
   * the firm default the personal override is cleared (saved as null) so the
   * user simply follows the firm default; otherwise it is saved as a personal
   * preference. A rotated session token is stored when the API returns one so
   * a subsequent reload stays authenticated. Returns the updated user.
   */
  public persistUserLanguage(
    user: IUser,
    hiringFirm: IHiringFirm,
    langCode: string
  ): Observable<IUser> {
    const firmDefaultLanguage = hiringFirm?.settings?.language || 'en';
    const languageToSave = langCode === firmDefaultLanguage ? null : langCode;

    const userData = {
      data: {
        id: user.id,
        type: 'users',
        attributes: {
          language: languageToSave
        }
      }
    };

    return this._users.patch(userData).pipe(
      tap((res: IUser) => {
        if (!!res.token) {
          this._commonEnvironments.setToken('candidate-token', res.token, false);
        }
      })
    );
  }

  /**
   * Reloads the app so a language change applies across the whole shell.
   * A runtime TranslateService.use() does not reliably re-render lazily loaded
   * views, so a reload guarantees a consistent switch; on bootstrap the root
   * AppComponent re-applies the persisted language.
   */
  public reloadPage(): void {
    window.location.reload();
  }
}
